import {E, Order, wsClient} from '../imports';

/**
 * OrdersManager is a singleton that manages the user's trading orders.
 * It handles WebSocket messages for order operations (GET_ORDERS, PLACE, CANCEL, MODIFY, REMOVE, UPDATE_ORDER).
 */
class OrdersManager {
    orders: Map<string, Order> = new Map();

    isConnected: boolean = false;

    // Callback fired when orders update
    onOrdersUpdated: ((orders: Map<string, Order>) => void) | null = null;


    /**
     * Initialize orders by requesting current data from server.
     * Should be called after WebSocket connection is established.
     */
    init = async (): Promise<void> => {
        // Register callback with WsClient
        wsClient.onOrderUpdate = this.handleOrderUpdate;

        await wsClient.sendRequest(
            E.WsRoute.ORDERS,
            E.WsOrdersCommand.GET_ALL,
            undefined,
            10
        )
        .then((ordersArray) => {
            // Convert array to Map keyed by order ID
            this.orders = new Map();
            if (Array.isArray(ordersArray)) {
                ordersArray.forEach((orderData: any) => {
                    const order = this.createOrderFromData(orderData);
                    this.orders.set(order.id, order);
                });
            }
            this.isConnected = true;
            this.onOrdersUpdated?.(this.orders);
            console.log(`[OrdersManager] Loaded ${this.orders.size} orders`);
        })
        .catch(error => {
            console.error('[OrdersManager] Failed to load orders:', error);
            throw error;
        });
    };

    /**
     * Disconnect and clear orders data.
     */
    disconnect = () => {
        this.orders.clear();
        this.isConnected = false;
        this.onOrdersUpdated?.(this.orders);
        console.log('[OrdersManager] Disconnected and cleared orders');
    };

    /**
     * Handle incoming WebSocket push message for order update (delta).
     * Payload contains only changed fields plus the order ID.
     */
    handleOrderUpdate = (payload: Partial<Order> & { id: string }) => {
        const { id } = payload;
        const existingOrder = this.orders.get(id);

        if (!existingOrder) {
            // New order - create from full data
            const newOrder = this.createOrderFromData(payload);
            this.orders.set(id, newOrder);
            console.log(`[OrdersManager] New order added: ${id}`);
        } else {
            // Delta update - merge changed fields
            this.mergeOrderDelta(existingOrder, payload);
            console.log(`[OrdersManager] Order updated: ${id}`, payload);
        }

        this.onOrdersUpdated?.(this.orders);
    };

    /**
     * Place a new order.
     * Returns the order ID if successful.
     */
    placeOrder = async (orderData: Partial<Order>): Promise<void> => {
        console.log('[OrdersManager] Placing order:', orderData);
        await wsClient.sendRequest(
            E.WsRoute.ORDERS,
            E.WsOrdersCommand.PLACE,
            orderData,
            10
        )
        .then(() =>{
            console.log('[OrdersManager] Order placed successfully');
        })
        .catch(error => {
            console.error('[OrdersManager] Failed to place order:', error);
            throw error;
        });
    };

    /**
     * Cancel an existing order.
     */
    cancelOrder = async (orderId: string): Promise<void> => {
        console.log('[OrdersManager] Cancelling order:', orderId);
        await wsClient.sendRequest(
            E.WsRoute.ORDERS,
            E.WsOrdersCommand.CANCEL,
            orderId,
            10
        )
        .then(() => {
            console.log('[OrdersManager] Order cancelled successfully');
        })
        .catch(error => {
            console.error('[OrdersManager] Failed to cancel order:', error);
            throw error;
        });
    };

    /**
     * Modify an existing order.
     */
    modifyOrder = async (orderData: Partial<Order> & { id: string }): Promise<void> => {
        console.log('[OrdersManager] Modifying order:', orderData);
        await wsClient.sendRequest(
            E.WsRoute.ORDERS,
            E.WsOrdersCommand.MODIFY,
            orderData,
            10
        )
        .then(() => {
            console.log('[OrdersManager] Order modified successfully');
        })
        .catch(error => {
            console.error('[OrdersManager] Failed to modify order:', error);
            throw error;
        });
    };

    /**
     * Remove a completed order from the list.
     */
    removeOrder = async (orderId: string): Promise<void> => {
        console.log('[OrdersManager] Removing order:', orderId);
        await wsClient.sendRequest(
            E.WsRoute.ORDERS,
            E.WsOrdersCommand.REMOVE,
            orderId,
            10
        )
        .then(() => {
            // Remove from local state
            this.orders.delete(orderId);
            this.onOrdersUpdated?.(this.orders);
            console.log('[OrdersManager] Order removed successfully');
        })
        .catch(error => {
            console.error('[OrdersManager] Failed to remove order:', error);
            throw error;
        }); 
    };

    /**
     * Create an Order instance from plain object data.
     */
    private createOrderFromData(data: any): Order {
        return new Order(
            data.id,
            data.status,
            data.instrument,
            data.side,
            data.type,
            data.quantity,
            data.timeInForce,
            data.createdAt,
            data.stats,
            data.takeProfit,
            data.stopLoss,
            data.trailDistance,
            data.triggeredAt,
            data.error
        );
    }

    /**
     * Merge delta update into existing order.
     * Only updates fields that are present in the delta.
     */
    private mergeOrderDelta(order: Order, delta: Partial<Order>) {
        // Update primitive fields
        if (delta.status !== undefined) order.status = delta.status;
        if (delta.quantity !== undefined) order.quantity = delta.quantity;
        if (delta.error !== undefined) order.error = delta.error;
        if (delta.triggeredAt !== undefined) order.triggeredAt = delta.triggeredAt;
        if (delta.timeInForce !== undefined) order.timeInForce = delta.timeInForce;

        // Merge stats object
        if (delta.stats && order.stats) {
            order.stats = { ...order.stats, ...delta.stats };
        } else if (delta.stats) {
            order.stats = delta.stats;
        }

        // Update triggers if provided
        if (delta.takeProfit !== undefined) order.takeProfit = delta.takeProfit;
        if (delta.stopLoss !== undefined) order.stopLoss = delta.stopLoss;
        if (delta.trailDistance !== undefined) order.trailDistance = delta.trailDistance;

        // Update instrument/side/type if provided (unlikely but handle it)
        if (delta.instrument) order.instrument = delta.instrument;
        if (delta.side !== undefined) order.side = delta.side;
        if (delta.type !== undefined) order.type = delta.type;
    }

    /**
     * Get orders for a specific symbol.
     */
    getOrdersForSymbol = (symbol: string): Order[] => {
        return Array.from(this.orders.values()).filter(
            order => order.instrument.symbol === symbol
        );
    };

    /**
     * Get active orders (not in terminal states).
     */
    getActiveOrders = (): Order[] => {
        return Array.from(this.orders.values()).filter(
            order => !order.isTerminal()
        );
    };
}

export const ordersManager = new OrdersManager();
